import { Button, CloseButton, Popup, RadioButton, TextInput } from '@consigli/facade';
import { EMAIL_PATTERN, useMultiEmailInput } from '@consigli/hooks';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiUsers } from 'react-icons/fi';

import { AttendeePickerPopover, type AttendeeOption } from './attendee-picker-popover';

import type { MeetingParticipant } from '../utils/export-types';

/** Mirrors `MAX_EMAIL_RECIPIENTS` in the meeting API's send-email schema. */
export const MAX_EMAIL_RECIPIENTS = 40;

export enum EmailContentOption {
  SUMMARY_ONLY = 'SUMMARY_ONLY',
  ACTION_ITEMS_ONLY = 'ACTION_ITEMS_ONLY',
  ALL_DETAILS = 'ALL_DETAILS',
}

interface EmailOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (option: EmailContentOption, recipients: string[]) => void;
  isLoading: boolean;
  /** Meeting attendees (organizer included) the user can pick recipients from. */
  attendees?: MeetingParticipant[];
}

export const EmailOptionsModal: FC<EmailOptionsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  attendees = [],
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(EmailContentOption.ALL_DETAILS);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const pickerAnchorRef = useRef<HTMLDivElement>(null);

  const validateEmail = useCallback(
    (email: string, current: string[]) => {
      if (current.length >= MAX_EMAIL_RECIPIENTS) {
        return t('meeting-secretary.meetings.email-recipient-limit', {
          max: MAX_EMAIL_RECIPIENTS,
        });
      }
      return EMAIL_PATTERN.test(email)
        ? undefined
        : t('meeting-secretary.meetings.email-invalid-email');
    },
    [t],
  );
  const { draft, draftError, onDraftChange, onKeyDown, onBlur, onPaste, removeAt } =
    useMultiEmailInput({
      value: recipients,
      onChange: setRecipients,
      validateEmail,
      duplicateMessage: t('meeting-secretary.meetings.email-duplicate-recipient'),
    });

  const attendeeOptions = useMemo<AttendeeOption[]>(() => {
    const seen = new Set<string>();
    const options: AttendeeOption[] = [];
    for (const p of attendees) {
      const email = p.email?.trim().toLowerCase();
      if (!email || !EMAIL_PATTERN.test(email) || seen.has(email)) continue;
      seen.add(email);
      options.push({ id: p.id, email, name: p.name, isOrganizer: p.role === 'organizer' });
    }
    return options;
  }, [attendees]);

  const selectedEmails = useMemo(
    () => new Set(recipients.map((email) => email.toLowerCase())),
    [recipients],
  );
  const isFull = recipients.length >= MAX_EMAIL_RECIPIENTS;

  const toggleAttendee = useCallback((email: string) => {
    setRecipients((prev) => {
      if (prev.some((r) => r.toLowerCase() === email)) {
        return prev.filter((r) => r.toLowerCase() !== email);
      }
      return prev.length >= MAX_EMAIL_RECIPIENTS ? prev : [...prev, email];
    });
  }, []);

  const toggleAllAttendees = useCallback(() => {
    setRecipients((prev) => {
      const attendeeEmails = new Set(attendeeOptions.map((a) => a.email));
      const present = new Set(prev.map((r) => r.toLowerCase()));
      const missing = attendeeOptions.filter((a) => !present.has(a.email)).map((a) => a.email);
      // Deselect when everyone is already in, or when there is no room left to add the rest.
      if (missing.length === 0 || prev.length >= MAX_EMAIL_RECIPIENTS) {
        return prev.filter((r) => !attendeeEmails.has(r.toLowerCase()));
      }
      return [...prev, ...missing].slice(0, MAX_EMAIL_RECIPIENTS);
    });
  }, [attendeeOptions]);

  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  // Reset modal state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelected(EmailContentOption.ALL_DETAILS);
      setRecipients([]);
      setIsPickerOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const hasDraft = draft.trim().length > 0;
  const hasRecipients = recipients.length > 0;
  const canSend = hasRecipients || (hasDraft && !draftError);
  const hasValidDraft = hasDraft && !draftError;
  const isOverLimit = recipients.length + (hasValidDraft ? 1 : 0) > MAX_EMAIL_RECIPIENTS;
  const isSendDisabled = isLoading || !canSend || isOverLimit;

  const handleSend = () => {
    // Construct final recipients list including uncommitted draft if valid
    const finalRecipients =
      hasDraft && !draftError ? [...recipients, draft.trim().toLowerCase()] : recipients;
    onConfirm(selected, finalRecipients);
  };

  return (
    <Popup>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-semibold text-day-neutral-dark">
            {t('meeting-secretary.meetings.email-options-title')}
          </h2>
          <CloseButton onClick={onClose} ariaLabel={t('meeting-secretary.meetings.cancel')} />
        </div>

        <p className="text-sm text-day-neutral-subtle">
          {t('meeting-secretary.meetings.email-options-description')}
        </p>

        {/* Content Options */}
        <div className="flex flex-col gap-3">
          <p className="text-sm font-medium text-day-neutral-dark">
            {t('meeting-secretary.meetings.email-what-to-send')}
          </p>
          <RadioButton
            name="email-content"
            value={EmailContentOption.SUMMARY_ONLY}
            label={t('meeting-secretary.meetings.email-option-summary-only')}
            checked={selected === EmailContentOption.SUMMARY_ONLY}
            onChange={() => setSelected(EmailContentOption.SUMMARY_ONLY)}
          />
          <RadioButton
            name="email-content"
            value={EmailContentOption.ACTION_ITEMS_ONLY}
            label={t('meeting-secretary.meetings.email-option-action-items-only')}
            checked={selected === EmailContentOption.ACTION_ITEMS_ONLY}
            onChange={() => setSelected(EmailContentOption.ACTION_ITEMS_ONLY)}
          />
          <RadioButton
            name="email-content"
            value={EmailContentOption.ALL_DETAILS}
            label={t('meeting-secretary.meetings.email-option-all-details')}
            checked={selected === EmailContentOption.ALL_DETAILS}
            onChange={() => setSelected(EmailContentOption.ALL_DETAILS)}
          />
        </div>

        {/* Recipient Email Input */}
        <div className="flex flex-col gap-2">
          <div className="flex items-end gap-2">
            <TextInput
              label={t('meeting-secretary.meetings.email-send-to')}
              placeholder={t('meeting-secretary.meetings.email-enter-recipient')}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              onPaste={onPaste}
              disabled={isLoading}
              className="flex-1"
            />
            {attendeeOptions.length > 0 && (
              <div ref={pickerAnchorRef} className="relative">
                <Button
                  secondary
                  rounded
                  type="button"
                  onClick={() => setIsPickerOpen((open) => !open)}
                  disabled={isLoading}
                  aria-haspopup="dialog"
                  aria-expanded={isPickerOpen}
                  className="flex h-10 items-center gap-2 whitespace-nowrap text-sm"
                >
                  <FiUsers size={14} aria-hidden="true" />
                  {t('meeting-secretary.meetings.email-attendees')}
                  <FiChevronDown size={14} aria-hidden="true" />
                </Button>
                {isPickerOpen && (
                  <AttendeePickerPopover
                    attendees={attendeeOptions}
                    selectedEmails={selectedEmails}
                    isFull={isFull}
                    anchorRef={pickerAnchorRef}
                    onToggle={toggleAttendee}
                    onToggleAll={toggleAllAttendees}
                    onClose={closePicker}
                  />
                )}
              </div>
            )}
          </div>
          {draftError && <p className="text-xs text-red-600">{draftError}</p>}
        </div>

        {/* Recipients List */}
        {recipients.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-day-neutral-dark">
              {t('meeting-secretary.meetings.email-recipients')} ({recipients.length}/
              {MAX_EMAIL_RECIPIENTS})
            </p>
            <div className="flex flex-wrap gap-2">
              {recipients.map((email, index) => (
                <div
                  key={email}
                  className="flex items-center gap-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  <span>{email}</span>
                  <CloseButton
                    onClick={() => removeAt(index)}
                    ariaLabel={t('meeting-secretary.meetings.remove')}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-center gap-3 mt-4">
          <Button
            secondary
            rounded
            onClick={onClose}
            disabled={isLoading}
            data-testid="cancel-button"
            className="w-28 h-10 text-sm"
          >
            {t('meeting-secretary.meetings.cancel')}
          </Button>
          <Button
            primary
            rounded
            onClick={handleSend}
            disabled={isSendDisabled}
            className="w-28 h-10 text-sm"
          >
            {isLoading
              ? t('meeting-secretary.meetings.email-sending')
              : t('meeting-secretary.meetings.send-email')}
          </Button>
        </div>
      </div>
    </Popup>
  );
};
